import { CatalogItemRecommendationSectionDiscriminant, CatalogItemRecommendationSectionTypename, PlaylistSuggestionDiscriminant, PlaylistSuggestionTypename } from '../../types';
const isPlaylistItemType = value => value === 'COURSE' || value === 'LESSON' || value === 'SHORT_VIDEO';
export const isPlaylistSuggestionSection = section => {
  if (!section || typeof section !== 'object') return false;
  return '__typename' in section && section.__typename === PlaylistSuggestionTypename || 'type' in section && section.type === PlaylistSuggestionDiscriminant;
};
export const isCatalogRecommendationSection = section => {
  if (!section || typeof section !== 'object') return false;
  return '__typename' in section && section.__typename === CatalogItemRecommendationSectionTypename || 'type' in section && section.type === CatalogItemRecommendationSectionDiscriminant;
};
export const normalizePlaylistItemType = type => {
  if (typeof type === 'string') {
    return isPlaylistItemType(type) ? type : undefined;
  }
  if (type && typeof type === 'object' && 'value' in type) {
    const {
      value
    } = type;
    return typeof value === 'string' && isPlaylistItemType(value) ? value : undefined;
  }
  return undefined;
};
export const normalizePlaylistSuggestion = section => {
  var _section$items;
  return Object.assign({}, section, {
    __typename: PlaylistSuggestionTypename,
    type: PlaylistSuggestionDiscriminant,
    items: ((_section$items = section.items) !== null && _section$items !== void 0 ? _section$items : []).map(item => {
      const normalizedType = normalizePlaylistItemType(item.type);
      return normalizedType ? Object.assign({}, item, {
        type: normalizedType
      }) : item;
    })
  });
};
const normalizeResponseSectionForRegistry = section => {
  if (isPlaylistSuggestionSection(section)) {
    return normalizePlaylistSuggestion(section);
  }
  if (isCatalogRecommendationSection(section)) {
    if (section.__typename === CatalogItemRecommendationSectionTypename) {
      return section;
    }
    return Object.assign({}, section, {
      __typename: CatalogItemRecommendationSectionTypename
    });
  }
  return section;
};
const normalizeMessagePayload = payload => {
  var _payload$dynamicSecti;
  if (!((_payload$dynamicSecti = payload.dynamicSectionContainers) !== null && _payload$dynamicSecti !== void 0 && _payload$dynamicSecti.length)) return payload;
  return Object.assign({}, payload, {
    dynamicSectionContainers: payload.dynamicSectionContainers.map(container => Object.assign({}, container, {
      responseSection: normalizeResponseSectionForRegistry(container.responseSection)
    }))
  });
};
const parsePlaylistDraftContext = contextSummary => {
  if (!contextSummary) return null;
  try {
    const parsed = JSON.parse(contextSummary);
    return parsed !== null && parsed !== void 0 && parsed.name ? parsed : null;
  } catch (_unused) {
    return null;
  }
};
export const getPlaylistDraftContext = (message, section) => {
  var _message$dynamicSecti;
  const containers = (_message$dynamicSecti = message === null || message === void 0 ? void 0 : message.dynamicSectionContainers) !== null && _message$dynamicSecti !== void 0 ? _message$dynamicSecti : [];
  const matchingContainer = containers.find(({
    responseSection
  }) => {
    var _responseSection$item, _section$items2;
    if (!isPlaylistSuggestionSection(responseSection)) return false;
    const responseItems = (_responseSection$item = responseSection.items) !== null && _responseSection$item !== void 0 ? _responseSection$item : [];
    const sectionItems = (_section$items2 = section.items) !== null && _section$items2 !== void 0 ? _section$items2 : [];
    if (responseItems.length !== sectionItems.length) return false;
    return responseItems.every((item, index) => {
      var _sectionItems$index;
      return item.itemId === ((_sectionItems$index = sectionItems[index]) === null || _sectionItems$index === void 0 ? void 0 : _sectionItems$index.itemId);
    });
  });
  return parsePlaylistDraftContext(matchingContainer === null || matchingContainer === void 0 ? void 0 : matchingContainer.contextSummary);
};
export const attachPlaylistDraftContext = (payload, textBlock) => {
  var _textBlock$entries, _normalizedPayload$dy;
  const additionalContentByCitationId = new Map(((_textBlock$entries = textBlock.entries) !== null && _textBlock$entries !== void 0 ? _textBlock$entries : []).map(entry => entry.citationId && entry.additionalContent ? [entry.citationId, entry.additionalContent] : null).filter(entry => entry !== null));
  const normalizedPayload = normalizeMessagePayload(payload);
  if (!additionalContentByCitationId.size || !((_normalizedPayload$dy = normalizedPayload.dynamicSectionContainers) !== null && _normalizedPayload$dy !== void 0 && _normalizedPayload$dy.length)) {
    return normalizedPayload;
  }
  return Object.assign({}, normalizedPayload, {
    dynamicSectionContainers: normalizedPayload.dynamicSectionContainers.map(container => {
      var _additionalContentByC;
      const contextSummary = (_additionalContentByC = additionalContentByCitationId.get(container.id)) !== null && _additionalContentByC !== void 0 ? _additionalContentByC : container.contextSummary;
      if (!contextSummary || container.contextSummary === contextSummary) {
        return container;
      }
      return Object.assign({}, container, {
        contextSummary,
        responseSection: isPlaylistSuggestionSection(container.responseSection) ? normalizePlaylistSuggestion(container.responseSection) : container.responseSection
      });
    })
  });
};
const findPlaylistEntry = chatMessage => {
  for (const block of (_chatMessage$blocks = chatMessage === null || chatMessage === void 0 ? void 0 : chatMessage.blocks) !== null && _chatMessage$blocks !== void 0 ? _chatMessage$blocks : []) {
    var _chatMessage$blocks;
    if (block.type !== 'DISPLAY_ONLY') continue;
    for (const entry of (_block$entries = block.entries) !== null && _block$entries !== void 0 ? _block$entries : []) {
      var _block$entries;
      if (entry.__typename === PlaylistSuggestionTypename || entry.type === PlaylistSuggestionDiscriminant) {
        return entry;
      }
    }
  }
  return null;
};
export const injectUncitedPlaylist = (payload, chatMessage) => {
  var _payload$dynamicSecti2, _playlistSection$item, _entry$citationId, _entry$additionalCont, _payload$sections, _payload$dynamicSecti3;
  const alreadyCited = ((_payload$dynamicSecti2 = payload.dynamicSectionContainers) !== null && _payload$dynamicSecti2 !== void 0 ? _payload$dynamicSecti2 : []).some(c => isPlaylistSuggestionSection(c.responseSection));
  if (alreadyCited) return payload;
  const entry = findPlaylistEntry(chatMessage);
  if (!entry) return payload;
  if (!entry.value || typeof entry.value !== 'object') return payload;
  const candidate = Object.assign({}, entry.value, {
    __typename: PlaylistSuggestionTypename
  });
  if (!isPlaylistSuggestionSection(candidate)) return payload;
  const playlistSection = normalizePlaylistSuggestion(candidate);
  if (!((_playlistSection$item = playlistSection.items) !== null && _playlistSection$item !== void 0 && _playlistSection$item.length)) return payload;
  const playlistContainer = {
    id: (_entry$citationId = entry.citationId) !== null && _entry$citationId !== void 0 ? _entry$citationId : entry.id,
    contextSummary: (_entry$additionalCont = entry.additionalContent) !== null && _entry$additionalCont !== void 0 ? _entry$additionalCont : '',
    responseSection: playlistSection
  };
  return Object.assign({}, payload, {
    sections: [...((_payload$sections = payload.sections) !== null && _payload$sections !== void 0 ? _payload$sections : []), playlistSection],
    dynamicSectionContainers: [...((_payload$dynamicSecti3 = payload.dynamicSectionContainers) !== null && _payload$dynamicSecti3 !== void 0 ? _payload$dynamicSecti3 : []), playlistContainer]
  });
};